import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import aircraftIconUrl from './assets/aircraft.svg';
import { fetchLiveAircraftPosition } from './adsbService';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import { defaultAppSettings, loadAppSettings } from './appSettings';
import { fetchOfficialTechLogsForBookings } from './officialTechLog';

const ADSB_CACHE_KEY = 'wingit_adsb_last_known_v2';

function normalizeRegistration(registration) {
  return (registration || '').toString().trim().toUpperCase().replace(/\s+/g, '');
}

function loadCachedAdsbPosition(registration) {
  try {
    const raw = localStorage.getItem(ADSB_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    const key = normalizeRegistration(registration);
    return cache[key] || null;
  } catch (err) {
    return null;
  }
}

function saveCachedAdsbPosition(registration, position) {
  try {
    const key = normalizeRegistration(registration);
    if (!key || !position) return;
    const raw = localStorage.getItem(ADSB_CACHE_KEY);
    const cache = raw ? JSON.parse(raw) : {};
    cache[key] = {
      lat: Number(position.lat),
      lng: Number(position.lng),
      heading: Number(position.heading) || 0,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(ADSB_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    // Ignore cache errors and continue without persistence.
  }
}

function formatAgeFromIso(isoTime) {
  if (!isoTime) return null;
  const t = Date.parse(isoTime);
  if (!Number.isFinite(t)) return null;
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 48) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatLocalTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function parseDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue}`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + Number(minutes || 0) * 60000);
}

function getBookingEndDate(row) {
  const explicitEnd = parseDateTime(row.end_date, row.end_time);
  if (explicitEnd) return explicitEnd;

  const start = parseDateTime(row.date, row.time || '00:00');
  if (!start) return null;
  return addMinutes(start, Number(row.duration) || 60);
}

function isMissingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('end_date') || message.includes('end_time') || message.includes('column');
}

function toBookingKey(value) {
  return String(value || '').trim();
}


// Real ADS-B map using Leaflet
function ADSBMap({ aircraft }) {
  const [coords, setCoords] = useState({ lat: null, lng: null, heading: 0 });
  const [liveStatus, setLiveStatus] = useState('fallback');
  const [cachedAge, setCachedAge] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchLatLng() {
      if (!aircraft) return;

      try {
        const live = await fetchLiveAircraftPosition(aircraft.registration);
        if (live && isMounted) {
          setCoords({ lat: live.lat, lng: live.lng, heading: Number(live.heading) || 0 });
          saveCachedAdsbPosition(aircraft.registration, live);
          setLiveStatus('live');
          setCachedAge(null);
          return;
        }
      } catch (error) {
        if (isMounted) setLiveStatus('fallback');
      }

      const cached = loadCachedAdsbPosition(aircraft.registration);
      if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
        if (isMounted) {
          setCoords({ lat: cached.lat, lng: cached.lng, heading: Number(cached.heading) || 0 });
          setLiveStatus('cached');
          setCachedAge(formatAgeFromIso(cached.updatedAt));
        }
        return;
      }

      if (aircraft?.lat && aircraft?.lng) {
        if (isMounted) {
          setCoords({ lat: aircraft.lat, lng: aircraft.lng, heading: 0 });
          setLiveStatus('fallback');
          setCachedAge(null);
        }
      } else if (aircraft?.icao_type) {
        // Try to look up by ICAO code in airports.csv
        const response = await fetch(process.env.PUBLIC_URL + '/airports.csv');
        const text = await response.text();
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const found = results.data.find(row => row.icao_code === aircraft.icao_type);
            if (found && isMounted) {
              setCoords({ lat: parseFloat(found.latitude_deg), lng: parseFloat(found.longitude_deg), heading: 0 });
              setLiveStatus('fallback');
              setCachedAge(null);
            } else if (isMounted) {
              setCoords({ lat: 51.4775, lng: -0.4614, heading: 0 }); // fallback: Heathrow
              setLiveStatus('fallback');
              setCachedAge(null);
            }
          }
        });
      } else {
        if (isMounted) {
          setCoords({ lat: 51.4775, lng: -0.4614, heading: 0 }); // fallback: Heathrow
          setLiveStatus('fallback');
          setCachedAge(null);
        }
      }
    }

    fetchLatLng();

    const pollId = window.setInterval(fetchLatLng, 15000);
    return () => {
      isMounted = false;
      window.clearInterval(pollId);
    };
  }, [aircraft]);

  // Custom aircraft icon
  const aircraftIcon = L.divIcon({
    className: 'aircraft-rotating-icon',
    html: `<img src="${aircraftIconUrl}" alt="Aircraft" style="width:32px;height:32px;transform:rotate(${coords.heading || 0}deg);transform-origin:center center;" />`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });

  return (
    <div style={{ background: '#23243a', borderRadius: 10, padding: '1.5rem', color: '#b6eaff', marginBottom: '2rem' }}>
      <h3 style={{ color: '#1ecfff', marginBottom: '1rem' }}>ADS-B Map</h3>
      <div>Current/last known location for: <strong>{aircraft?.registration || 'N/A'}</strong></div>
      <div style={{ marginTop: 6, fontSize: 13, color: liveStatus === 'live' ? '#7dffb8' : liveStatus === 'cached' ? '#9bd3ff' : '#ffd479' }}>
        {liveStatus === 'live'
          ? 'Live ADS-B position'
          : liveStatus === 'cached'
            ? `Last known ADS-B position${cachedAge ? ` (${cachedAge})` : ''}`
            : 'Fallback position (no live ADS-B hit)'}
      </div>
      <div style={{ height: 300, width: '100%', marginTop: 12, borderRadius: 8, overflow: 'hidden' }}>
        {coords.lat && coords.lng && (
          <MapContainer center={[coords.lat, coords.lng]} zoom={8} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[coords.lat, coords.lng]} icon={aircraftIcon}>
              <Popup>
                {aircraft?.registration || 'Aircraft'}
              </Popup>
            </Marker>
          </MapContainer>
        )}
      </div>
    </div>
  );
}

function AircraftOverview({ aircraft, stats, loading, defects, maintenanceSettings }) {
  const checkIntervalHours = Number(maintenanceSettings?.checkIntervalHours) || 50;
  const warningWindowHours = Number(maintenanceSettings?.warningWindowHours) || 5;
  const totalHours = Number(stats?.totalHours || 0);
  const hoursIntoCycle = totalHours % checkIntervalHours;
  const hoursToCheck = totalHours === 0 ? checkIntervalHours : (checkIntervalHours - hoursIntoCycle) % checkIntervalHours;
  const dueNow = totalHours > 0 && hoursToCheck === 0;
  const dueSoon = !dueNow && hoursToCheck <= warningWindowHours;
  const openDefects = getAircraftOpenDefects(aircraft?.id, defects);

  const status = String(aircraft?.status || 'serviceable').toLowerCase();
  const statusLabel = status === 'aog'
    ? 'AOG'
    : status === 'deferred'
      ? 'Deferred'
      : 'Serviceable';

  const statusColor = status === 'aog'
    ? '#ff8f8f'
    : status === 'deferred'
      ? '#ffd27a'
      : '#8ff4b6';

  return (
    <div style={{ background: '#23243a', border: '1px solid #2f466d', borderRadius: 14, padding: '1.25rem', color: '#d7ebff', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#ffffff', fontSize: '1.8rem', fontWeight: 900, lineHeight: 1 }}>
            {aircraft?.registration || 'Aircraft'}
          </div>
          <div
  style={{
    color: '#8db7e6',
    fontSize: '0.8rem',
    marginTop: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: 700,
  }}
>
  {aircraft?.icao_type || 'Type not set'}
</div>
        </div>

        <div style={{ color: statusColor, border: `1px solid ${statusColor}`, borderRadius: 999, padding: '0.35rem 0.7rem', fontWeight: 900, fontSize: '0.78rem' }}>
          {statusLabel.toUpperCase()}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: '1rem' }}>
        <div style={aircraftOverviewTileStyle}>
          <div style={aircraftOverviewLabelStyle}>LAST FLIGHT</div>
          <div style={aircraftOverviewValueStyle}>
  {loading
    ? 'Loading...'
    : stats?.lastFlightDate
      ? (() => {
          const parsed = new Date(stats.lastFlightDate);
          return Number.isFinite(parsed.getTime())
            ? (
    <>
      <div>
        {parsed.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </div>
      <div style={{ marginTop: 4, fontSize: '0.9rem', color: '#9fdcff' }}>
        {formatLocalTime(parsed)}
      </div>
    </>
  )
            : String(stats.lastFlightDate);
        })()
      : 'N/A'}
</div>
        </div>

        <div style={aircraftOverviewTileStyle}>
          <div style={aircraftOverviewLabelStyle}>NEXT CHECK</div>
          <div style={{ ...aircraftOverviewValueStyle, color: dueNow ? '#ff8f8f' : dueSoon ? '#ffd27a' : '#8ff4b6' }}>
            {loading
              ? 'Loading...'
              : dueNow
                ? 'Due now'
                : `${hoursToCheck.toFixed(1)}h remaining`}
          </div>
        </div>

        <div style={aircraftOverviewTileStyle}>
          <div style={aircraftOverviewLabelStyle}>OPEN DEFECTS</div>
          <div style={{ ...aircraftOverviewValueStyle, color: openDefects.length > 0 ? '#ffd27a' : '#8ff4b6' }}>
            {openDefects.length}
          </div>
        </div>
      </div>
    </div>
  );
}

function getAircraftOpenDefects(aircraftId, defects) {
  return (defects || []).filter((defect) => (
    String(defect.aircraft_id) === String(aircraftId) &&
    String(defect.status || '').toLowerCase() !== 'rectified'
  ));
}
function AircraftTechLogSheet({ aircraft, stats, loading, rows, maintenanceSettings }) {
  
  
  
  
  
  
  

  return (
    <div style={{ background: '#ffffff', color: '#111827', borderRadius: 10, padding: '1rem', marginBottom: '2rem', border: '1px solid #d1d5db' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ color: '#111827', margin: 0 }}>Aircraft Tech Log</h3>
          <div style={{ color: '#4b5563', marginTop: 4 }}>
            {aircraft?.registration || 'Aircraft'} {aircraft?.icao_type ? `• ${aircraft.icao_type}` : ''}
          </div>
        </div>

        
      </div>

      

      {loading ? (
        <div style={{ color: '#4b5563' }}>Loading aircraft tech log...</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#4b5563' }}>No completed tech log entries found for this aircraft yet.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #d1d5db', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980, background: '#ffffff' }}>
            <thead>
              <tr>
                {['Date', 'Pilot', 'Leg', 'From', 'To', 'Flight Time', 'Hobbs Out', 'Hobbs In', 'Fuel', 'Oil', 'Defect'].map((heading) => (
                  <th key={heading} style={techLogSheetHeadStyle}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const log = row.log || {};
                const legs = Array.isArray(log.legs) && log.legs.length > 0
                  ? log.legs
                  : [{ legNumber: 1 }];

                return legs.map((leg, index) => (
                  <tr key={`${row.bookingId}_${index}`}>
                    <td style={techLogSheetCellStyle}>
  {row.flightDate
    ? new Date(row.flightDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '-'}
</td>

<td style={techLogSheetCellStyle}>
  {String(row.pilotName || '-')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')}
</td>
                    <td style={techLogSheetCellStyle}>{leg.legNumber || index + 1}</td>
                    <td style={techLogSheetCellStyle}>{leg.from || log.departureAirfield || '-'}</td>
                    <td style={techLogSheetCellStyle}>{leg.to || log.arrivalAirfield || '-'}</td>
                    <td style={techLogSheetCellStyle}>{leg.flightTime || log.flightTime || '-'}</td>
                    <td style={techLogSheetCellStyle}>{leg.hobbsOut || leg.hobbsStart || log.hobbsStart || '-'}</td>
                    <td style={techLogSheetCellStyle}>{leg.hobbsIn || leg.hobbsEnd || log.hobbsEnd || '-'}</td>
                    <td style={techLogSheetCellStyle}>{leg.fuelUpliftLitres || log.fuelUpliftLitres || '0'}L</td>
                    <td style={techLogSheetCellStyle}>{leg.oilUpliftLitres || log.oilUpliftLitres || '0'}L</td>
                    <td
  style={{
    ...techLogSheetCellStyle,
    fontWeight: leg.defect || log.defect || log.snag ? 700 : 400,
  }}
>
  {leg.defect || log.defect || log.snag || '-'}
</td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}







const techLogSheetHeadStyle = {
  background: '#e5e7eb',
  color: '#111827',
  border: '1px solid #d1d5db',
  padding: '0.55rem',
  fontSize: '0.78rem',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const techLogSheetCellStyle = {
  color: '#111827',
  border: '1px solid #e5e7eb',
  padding: '0.5rem',
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
};
const aircraftOverviewTileStyle = {
  background: '#1a2338',
  border: '1px solid #35507f',
  borderRadius: 10,
  padding: '0.85rem',
};

const aircraftOverviewLabelStyle = {
  color: '#8db7e6',
  fontSize: '0.72rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  marginBottom: 6,
};

const aircraftOverviewValueStyle = {
  color: '#ffffff',
  fontSize: '1rem',
  fontWeight: 850,
};
export default function ViewAircraft() {
  const navigate = useNavigate();
  const session = useSession();
  const user = session?.user;
  const organisationId =
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    user?.user_metadata?.organisation_id ||
    user?.app_metadata?.organisation_id ||
    '';
  const [aircraftList, setAircraftList] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [techStats, setTechStats] = useState(null);
  const [techStatsLoading, setTechStatsLoading] = useState(false);
  const [officialTechRows, setOfficialTechRows] = useState([]);
  const [officialTechLoading, setOfficialTechLoading] = useState(false);
  const [aircraftDefects, setAircraftDefects] = useState([]);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const appSettings = useMemo(
    () =>
      organisationId
        ? loadAppSettings(organisationId)
        : defaultAppSettings,
    [organisationId]
  );

  useEffect(() => {
    async function fetchAircraft() {
      setLoading(true);
      setError('');
      const { data, error } = await supabase
  .from('aircraft')
  .select('id, registration, icao_type, recording_method, status');
      if (error) setError('Could not load aircraft list.');
      else setAircraftList(data || []);
      const { data: defectRows } = await supabase
  .from('aircraft_defects')
  .select('id, aircraft_id, severity, details, status')
  .neq('status', 'rectified');

setAircraftDefects(defectRows || []);
      setLoading(false);
    }
    fetchAircraft();
  }, []);

  useEffect(() => {
    async function fetchTechStats() {
      if (!selectedAircraft?.id) {
        setTechStats(null);
        return;
      }

      setTechStatsLoading(true);
      let { data, error } = await supabase
        .from('bookings')
        .select('id, user_id, date, time, end_date, end_time, duration')
        .eq('aircraft_id', selectedAircraft.id);

      if (error && isMissingEndColumnsError(error)) {
        const fallback = await supabase
          .from('bookings')
          .select('id, user_id, date, time, duration')
          .eq('aircraft_id', selectedAircraft.id);

        data = (fallback.data || []).map((row) => ({
          ...row,
          end_date: null,
          end_time: null,
        }));
        error = fallback.error;
      }

      if (error) {
        setTechStats({ totalFlights: 0, totalHours: 0, uniquePilots: 0, lastFlightDate: 'N/A' });
        setTechStatsLoading(false);
        return;
      }

      const rows = data || [];
      const totalFlights = rows.length;
      const totalMinutes = rows.reduce((sum, row) => {
        const mins = Number(row.duration);
        return sum + (Number.isFinite(mins) && mins > 0 ? mins : 60);
      }, 0);
      const totalHours = totalMinutes / 60;
      const uniquePilots = new Set(rows.map(r => r.user_id).filter(Boolean)).size;
      const lastFlightDate = rows.length
        ? rows
            .map(getBookingEndDate)
            .filter(Boolean)
            .sort((a, b) => a - b)
            .slice(-1)[0]
        : null;

      setTechStats({ totalFlights, totalHours, uniquePilots, lastFlightDate: lastFlightDate ? `${formatLocalDate(lastFlightDate)} ${formatLocalTime(lastFlightDate)}` : 'N/A' });
      setTechStatsLoading(false);
    }

    fetchTechStats();
  }, [selectedAircraft]);

  useEffect(() => {
    async function fetchOfficialLogsForAircraft() {
      if (!selectedAircraft?.id) {
        setOfficialTechRows([]);
        return;
      }

      setOfficialTechLoading(true);

      let { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, user_id, date, time, end_date, end_time, duration')
        .eq('aircraft_id', selectedAircraft.id)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (bookingsError && isMissingEndColumnsError(bookingsError)) {
        const fallback = await supabase
          .from('bookings')
          .select('id, user_id, date, time, duration')
          .eq('aircraft_id', selectedAircraft.id)
          .order('date', { ascending: false })
          .order('time', { ascending: false });

        bookingsData = (fallback.data || []).map((row) => ({
          ...row,
          end_date: null,
          end_time: null,
        }));
        bookingsError = fallback.error;
      }

      if (bookingsError) {
        setOfficialTechRows([]);
        setOfficialTechLoading(false);
        return;
      }

      const bookingRows = bookingsData || [];
      const bookingIds = bookingRows.map((row) => toBookingKey(row.id)).filter(Boolean);

      const [officialResult, usersResult] = await Promise.all([
        fetchOfficialTechLogsForBookings(bookingIds),
        supabase.from('users').select('id, full_name, email'),
      ]);

      const officialMap = {};
      (officialResult.data || []).forEach((row) => {
        officialMap[toBookingKey(row.booking_id)] = row.data || {};
      });

      const userMap = {};
      (usersResult.data || []).forEach((row) => {
        userMap[String(row.id)] = row;
      });

      const rows = bookingRows.map((row) => {
        const pilot = userMap[String(row.user_id)];
        return {
          bookingId: toBookingKey(row.id),
          flightDate: row.date || 'Unknown date',
          flightTime: row.time || '',
          pilotName: pilot?.full_name || pilot?.email || row.user_id || 'Unknown pilot',
          log: officialMap[toBookingKey(row.id)] || {},
        };
      });

      setOfficialTechRows(rows);
      setOfficialTechLoading(false);
    }

    fetchOfficialLogsForAircraft();
  }, [selectedAircraft]);

  // When baseIcao changes, auto-fill lat/lng

  // ...existing code...
 
    return (
      <div style={{ maxWidth: 700, margin: '3rem auto', background: '#181a2a', borderRadius: 16, padding: '2.5rem 2rem', color: 'white', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
        <button onClick={() => navigate('/dashboard')} style={{ marginBottom: '1.2rem', background: '#23243a', color: '#b6eaff', border: '1px solid #1ecfff', borderRadius: 8, padding: '0.4rem 1.2rem', cursor: 'pointer', fontWeight: 600 }}>&#8592; Home</button>
        <h2 style={{ color: '#1ecfff', fontWeight: 700, fontSize: '2rem', marginBottom: '1.5rem' }}>View Aircraft</h2>
        {loading ? (
        <div style={{ color: '#b6eaff' }}>Loading aircraft...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 220, width: '100%', maxWidth: 260 }}>
            <h4 style={{ color: '#b6eaff', marginBottom: 12 }}>Aircraft List</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {aircraftList.map(a => (
                <li key={a.id} style={{ marginBottom: 10 }}>
                  <button
                    onClick={() => setSelectedAircraft(a)}
                    style={{ background: selectedAircraft?.id === a.id ? '#1ecfff' : '#23243a', color: selectedAircraft?.id === a.id ? '#181a2a' : '#b6eaff', border: 'none', borderRadius: 8, padding: '0.5rem 1.2rem', fontWeight: 600, cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  >
                    {a.registration} {a.icao_type ? `(${a.icao_type})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div style={{ flex: 1 }}>
            {selectedAircraft ? (
  <>
    <AircraftOverview
      aircraft={selectedAircraft}
      stats={techStats}
      loading={techStatsLoading}
      defects={aircraftDefects}
      maintenanceSettings={appSettings.maintenance}
    />

    

    <AircraftTechLogSheet
  aircraft={selectedAircraft}
  stats={techStats}
  loading={officialTechLoading}
  rows={officialTechRows}
  activeRecordingMethod={selectedAircraft?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs'}
  maintenanceSettings={appSettings.maintenance}
/>
<div style={{ marginBottom: '2rem', textAlign: 'center' }}>
  <button
    type="button"
    onClick={() => setTrackingOpen((prev) => !prev)}
    style={{
      background: '#23243a',
      color: '#b6eaff',
      border: '1px solid #1ecfff',
      borderRadius: 10,
      padding: '0.55rem 1rem',
      fontWeight: 800,
      display: 'inline-block',
      marginLeft: 0,
      cursor: 'pointer',
      width: 'auto',
textAlign: 'center',
marginTop: '1rem',
    }}
  >
    {trackingOpen ? 'HIDE AIRCRAFT TRACKING' : 'SHOW AIRCRAFT TRACKING'}
  </button>

  {trackingOpen && (
    <div style={{ marginTop: '1rem' }}>
      <ADSBMap aircraft={selectedAircraft} />
    </div>
  )}
</div>
                {/* Add other docs/components here */}
              </>
            ) : (
              <div style={{ color: '#b6eaff' }}>Select an aircraft to view details.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
